import React from 'react'
import { Spin, theme } from 'antd'
import { useIntl } from 'react-intl'
import { GoogleDirectoryChannelEvent, IGoogleDirectoryChannelStatus } from '@vacationtracker/shared/types/google'
import { radius, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { Banner } from '../../design-system/Banner'
import { Button } from '../../design-system/Button'
import { Stack } from '../../design-system/Stack'
import { StatusBadge } from '../../design-system/StatusBadge'
import { Text } from '../../design-system/Text'

export interface IGoogleDirectoryConnectionProps {
  /** null while the status is still loading */
  channels: IGoogleDirectoryChannelStatus[] | null
  isRefreshing: boolean
  onRefresh: () => void
}

const EVENT_LABEL_IDS: Record<GoogleDirectoryChannelEvent, string> = {
  add: 'googleConnection.eventAdd',
  update: 'googleConnection.eventUpdate',
  delete: 'googleConnection.eventDelete',
  undelete: 'googleConnection.eventUndelete',
}

export function GoogleDirectoryConnection({ channels, isRefreshing, onRefresh }: IGoogleDirectoryConnectionProps) {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const divider = `1px solid ${token.colorBorderSecondary}`
  const isDegraded = Boolean(channels?.some((channel) => !channel.connected))

  return (
    <Stack gap="sm">
      <Text weight="bold">{formatMessage({ id: 'googleConnection.title' })}</Text>
      <Text tone="secondary">{formatMessage({ id: 'googleConnection.description' })}</Text>

      {!channels && <Spin size="small" />}

      {channels && (
        <div style={{ border: divider, borderRadius: radius.base }}>
          {channels.map((channel, index) => (
            <div
              key={channel.event}
              style={{
                padding: `${spacing.sm}px ${spacing.base}px`,
                borderTop: index > 0 ? divider : undefined,
              }}
            >
              <Stack direction="horizontal" justify="space-between" align="center" gap="sm" wrap>
                <Text>{formatMessage({ id: EVENT_LABEL_IDS[channel.event] })}</Text>
                <StatusBadge tone={channel.connected ? 'success' : 'warning'}>
                  {formatMessage({ id: channel.connected ? 'googleConnection.statusConnected' : 'googleConnection.statusNotConnected' })}
                </StatusBadge>
              </Stack>
            </div>
          ))}
        </div>
      )}

      {isDegraded && <Banner tone="warning" message={formatMessage({ id: 'googleConnection.degradedBanner' })} />}

      <Stack direction="horizontal">
        <Button variant="outline" loading={isRefreshing} onClick={onRefresh}>
          {formatMessage({ id: 'googleConnection.refreshButton' })}
        </Button>
      </Stack>
    </Stack>
  )
}

export default GoogleDirectoryConnection
